/**
 * F36 (Wave 2E) — Admin clubs moderation queue page.
 *
 * Route: /clubs
 * Permission: clubs:read (super_admin + support + content_moderator)
 *
 * Table view of every club account (account_kind='club') — any status (active /
 * suspended / disabled). Status filter chips above the table (mirrors the F29
 * AdminTeamsPage chip pattern). Name search via SearchInput.
 *
 * A KPI strip surfaces active / suspended / verified counts. The Verified
 * column shows the club's F09 v2 tick (inert) from the `verified` boolean.
 * Row click → /clubs/:clubId detail page.
 *
 * F09 v2 retired the F36 "Verify club" action and its "Awaiting verification"
 * filter: the club tick is an F09 request decided under `/verifications`.
 * Moderation actions (Suspend / Reinstate) live on the detail page and are
 * additionally gated client-side on `clubs:moderate`; the server re-enforces.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Avatar,
  Banner,
  Button,
  Chip,
  EmptyState,
  SearchInput,
  Skeleton,
  Stat,
  StatusChip,
  type StatusChipStatus,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type { ClubDetail } from '@bravemove/types';
import {
  adminClubsApi,
  type AdminClubRow,
  type AdminClubsSearchOpts,
  type AdminClubStatusFilter,
} from '../../api/clubs.js';
import { translateApiError } from '../../auth/errors.js';
import { PageHeader } from '../../components/PageHeader';
import { InertTick } from '../../components/verification/EntityVerificationCard';
import { usePageKpis } from '../../hooks/usePageKpis';

const PAGE_LIMIT = 25;

type ClubFilter = AdminClubStatusFilter;
const DEFAULT_FILTER: ClubFilter = 'active';

const FILTER_CHIPS: ReadonlyArray<{ value: ClubFilter; labelKey: string }> = [
  { value: 'active', labelKey: 'admin:club.queue.filter_active' },
  { value: 'suspended', labelKey: 'admin:club.queue.filter_suspended' },
  { value: 'disabled', labelKey: 'admin:club.queue.filter_disabled' },
  { value: 'all', labelKey: 'admin:club.queue.filter_all' },
];

const STATUS_CHIP_MAP: Record<ClubDetail['status'], StatusChipStatus> = {
  active: 'approved',
  suspended: 'in_review',
  disabled: 'rejected',
};

function buildSearchOpts(filter: ClubFilter, q: string | null): AdminClubsSearchOpts {
  const opts: AdminClubsSearchOpts = { limit: PAGE_LIMIT };
  const withQ = q ? { ...opts, q } : opts;
  if (filter === 'all') return withQ;
  return { ...withQ, status: filter };
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AdminClubsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawFilter = searchParams.get('filter');
  const filterParam: ClubFilter =
    FILTER_CHIPS.find((c) => c.value === rawFilter)?.value ?? DEFAULT_FILTER;
  const qParam = searchParams.get('q');
  const [qDraft, setQDraft] = useState<string>(qParam ?? '');

  // F61 (A3) — one unaudited `GET /admin/page-kpis` (was 2–4 audited
  // `perPage=1` list reads); refreshed after any admin action.
  const kpiCounts = usePageKpis('clubs');

  const [rows, setRows] = useState<readonly AdminClubRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(
    async (opts: AdminClubsSearchOpts) => {
      setLoading(true);
      setRows([]);
      setNextCursor(null);
      setError(null);
      try {
        const data = await adminClubsApi.search(opts);
        setRows(data.data);
        setNextCursor(data.nextCursor);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList(buildSearchOpts(filterParam, qParam));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParam, qParam]);

  /**
   * Cursor-driven "Load more" (TCR-005) — the route returns `nextCursor`
   * (keyset on (created_at, id)) but the page previously fetched only the
   * first 25 rows with no way to advance; clubs beyond the first page were
   * silently unreachable. Mirrors the email-suppressions cursor pattern.
   */
  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const data = await adminClubsApi.search({
        ...buildSearchOpts(filterParam, qParam),
        cursor: nextCursor,
      });
      setRows((prev) => [...prev, ...data.data]);
      setNextCursor(data.nextCursor);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, filterParam, qParam, t]);

  // Debounce the search input → URL → effect chain.
  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = qDraft.trim();
      const current = qParam ?? '';
      if (trimmed === current) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        return next;
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [qDraft, qParam, setSearchParams]);

  const onFilterChange = (value: ClubFilter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== DEFAULT_FILTER) next.set('filter', value);
      else next.delete('filter');
      return next;
    });
  };

  const columns = useMemo<readonly TableColumn<AdminClubRow>[]>(
    () => [
      {
        key: 'name',
        header: t('admin:club.queue.column_name'),
        cell: (row) => (
          <div className="flex items-center gap-3 min-w-0">
            <Avatar name={row.displayName} size="sm" />
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-[13px] text-ink truncate">{row.displayName}</span>
              <span className="text-[11px] text-ink-3 font-mono tracking-wide">
                {row.countryCode}
              </span>
            </div>
          </div>
        ),
      },
      {
        key: 'owner',
        header: t('admin:club.queue.column_owner'),
        cell: (row) =>
          row.ownerDisplayName ? (
            <div className="flex items-center gap-2 min-w-0">
              <Avatar name={row.ownerDisplayName} size="sm" />
              <span className="text-[12.5px] text-ink-2 truncate">{row.ownerDisplayName}</span>
            </div>
          ) : (
            <span className="text-[12.5px] text-ink-3">—</span>
          ),
      },
      {
        key: 'status',
        header: t('admin:club.queue.column_status'),
        cell: (row) => (
          <StatusChip
            size="sm"
            status={STATUS_CHIP_MAP[row.status]}
            pulse={row.status === 'suspended'}
            label={t(`club:status.${row.status}`)}
          />
        ),
      },
      {
        key: 'verified',
        header: t('admin:club.queue.column_verified'),
        // F09 v2 (§13 row 21) — the inert club tick, from the boolean only.
        cell: (row) =>
          row.verified === true ? (
            <InertTick verified entityType="club" testId={`club-row-tick-${row.id}`} />
          ) : (
            <span className="text-[12.5px] text-ink-3">{t('admin:club.queue.verified_no')}</span>
          ),
      },
      {
        key: 'teams',
        header: t('admin:club.queue.column_teams'),
        cell: (row) => (
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-2 tabular-nums">
            <UIIcon name="team" size={13} color="currentColor" />
            {row.teamCount}
          </span>
        ),
      },
      {
        key: 'trainers',
        header: t('admin:club.queue.column_trainers'),
        cell: (row) => (
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-2 tabular-nums">
            <UIIcon name="whistle" size={13} color="currentColor" />
            {row.trainerCount}
          </span>
        ),
      },
      {
        key: 'created',
        header: t('admin:club.queue.column_created'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-3">{formatDate(row.createdAt, locale)}</span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6">
      <PageHeader title={t('admin:club.queue.title')} subtitle={t('admin:club.queue.subtitle')} />

      {/* KPI strip */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="clubs-kpi-strip">
        <Stat
          label={t('admin:club.queue.kpi.active')}
          value={kpiCounts === null ? '—' : kpiCounts.active}
          icon={<UIIcon name="team" size={16} color="currentColor" />}
        />
        <Stat
          label={t('admin:club.queue.kpi.suspended')}
          value={kpiCounts === null ? '—' : kpiCounts.suspended}
          icon={<UIIcon name="lock" size={16} color="currentColor" />}
        />
        <Stat
          label={t('admin:club.queue.kpi.verified')}
          value={kpiCounts === null ? '—' : kpiCounts.verified}
          icon={<UIIcon name="shield" size={16} color="currentColor" />}
        />
      </div>

      {/* Toolbar — status filter chips (left) + search (right). A bare row that
          stacks below `lg` so 5 chips + the search never crowd into a ragged
          two-line wrap (matches the F29 teams / trainer-verifications pattern). */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div
          role="tablist"
          aria-label={t('admin:club.queue.filter_all')}
          className="flex flex-wrap items-center gap-2"
        >
          {FILTER_CHIPS.map((chip) => (
            <Chip
              key={chip.value}
              selected={filterParam === chip.value}
              onClick={() => onFilterChange(chip.value)}
            >
              {t(chip.labelKey)}
            </Chip>
          ))}
        </div>

        <div className="lg:ms-auto lg:w-72">
          <SearchInput
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            placeholder={t('admin:club.queue.search_placeholder')}
            aria-label={t('admin:club.queue.search_placeholder')}
            data-testid="clubs-queue-search"
            className="w-full"
          />
        </div>
      </div>

      {error ? (
        <div data-testid="clubs-queue-error">
          <Banner tone="danger">{error}</Banner>
        </div>
      ) : null}

      {loading ? (
        <div className="space-y-3" data-testid="clubs-queue-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="team" size={36} color="currentColor" />}
          title={t('admin:club.queue.empty_title')}
          description={t('admin:club.queue.empty_body')}
        />
      ) : (
        <>
          <Table
            data={rows as AdminClubRow[]}
            columns={columns as TableColumn<AdminClubRow>[]}
            rowKey={(row) => row.id}
            onRowPress={(row) => navigate(`/clubs/${row.id}`)}
            empty={t('admin:club.queue.empty_title')}
          />
          {nextCursor !== null ? (
            <div className="flex justify-center pt-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void loadMore()}
                loading={loadingMore}
                disabled={loadingMore}
                data-testid="clubs-queue-load-more"
              >
                {t('admin:club.queue.load_more')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
